import type { NodeType, Severity } from "../types";

/**
 * UI-only owned-tool hints. These never affect scoring and never extend the
 * scorer signatures (canon decision 2A) - they only surface a visual aid on the
 * task screens when the matching tool is owned.
 */

const HIGH_SIGNIFICANCE: ReadonlySet<Severity> = new Set<Severity>(["high", "critical"]);

/**
 * log_filter: indices of every high-significance (high or critical) log event;
 * the screen dims the rest. (severity_highlighter has no engine part: it shows
 * the severity badge on every row before submit.)
 */
export function highlightedLogIndices(events: readonly { severity: Severity }[]): number[] {
  const out: number[] = [];
  events.forEach((event, index) => {
    if (HIGH_SIGNIFICANCE.has(event.severity)) {
      out.push(index);
    }
  });
  return out;
}

/** Node types that face the outside world (internet, contractors, cloud). */
export const EXTERNAL_NODE_TYPES: ReadonlySet<NodeType> = new Set<NodeType>([
  "public_site",
  "admin_panel",
  "vendor_access",
  "cloud_storage",
]);

function isExposed(node: { type: NodeType }): boolean {
  return EXTERNAL_NODE_TYPES.has(node.type);
}

/**
 * network_scanner: ids of every externally reachable node, in map order. They
 * are candidates, not answers - a public storefront is exposed but usually
 * fine, while an internal database can be risky without being flagged.
 */
export function scannerExposedNodeIds(nodes: readonly { id: string; type: NodeType }[]): string[] {
  return nodes.filter(isExposed).map((node) => node.id);
}

export type ExternalAccess =
  | { kind: "direct" }
  | { kind: "path"; via: string[] }
  | { kind: "unreachable" };

type GraphNode = { id: string; type: NodeType; connections: readonly string[] };

function undirectedAdjacency(nodes: readonly GraphNode[]): Map<string, string[]> {
  const adjacency = new Map<string, string[]>(nodes.map((node) => [node.id, []]));
  for (const node of nodes) {
    for (const other of node.connections) {
      if (!adjacency.has(other)) {
        continue;
      }
      adjacency.get(node.id)!.push(other);
      adjacency.get(other)!.push(node.id);
    }
  }
  return adjacency;
}

/**
 * access_matrix_viewer: how the outside world reaches a node. Exposed nodes are
 * reachable directly; for internal nodes the shortest chain from any exposed
 * node is returned (multi-source BFS over the undirected connection graph),
 * ending with the node itself; isolated nodes are unreachable.
 */
export function externalAccessPath(nodes: readonly GraphNode[], targetId: string): ExternalAccess {
  const target = nodes.find((node) => node.id === targetId);
  if (!target) {
    return { kind: "unreachable" };
  }
  if (isExposed(target)) {
    return { kind: "direct" };
  }
  const adjacency = undirectedAdjacency(nodes);
  const previous = new Map<string, string | null>();
  const queue: string[] = [];
  for (const node of nodes) {
    if (isExposed(node)) {
      previous.set(node.id, null);
      queue.push(node.id);
    }
  }
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === targetId) {
      const via: string[] = [];
      for (let step: string | null = current; step !== null; step = previous.get(step) ?? null) {
        via.unshift(step);
      }
      return { kind: "path", via };
    }
    for (const next of adjacency.get(current) ?? []) {
      if (!previous.has(next)) {
        previous.set(next, current);
        queue.push(next);
      }
    }
  }
  return { kind: "unreachable" };
}
